import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { ROUTES } from '@/config/routes';
import { adminSidebar } from '@/config/sidebarConfig';
import { getAdminStatsApi } from '@/lib/api/usersApi';
import { getMfaStatusApi } from '@/lib/auth/authApi';
import { queryKeys } from '@/lib/query/queryKeys';
import { useAuthStore } from '@/stores/auth-store';
import { formatDateAbbreviatedFr } from '@shared';
import { SubPlan } from '@shared/enums/domain.enum';
import type { AdminStatsMonthlyRevenue, AdminStatsRecentOrg, AdminStatsRecentPayment } from '@shared/types/entities';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowRight,
  BarChart3,
  Briefcase,
  Building,
  CheckCircle,
  CreditCard,
  Crown,
  Euro,
  ShieldCheck,
  TrendingUp,
  Trophy,
  UserCheck,
  Users,
  Zap,
} from 'lucide-react';

import DashboardLayout from '../../components/layout/DashboardLayout';
import PageHeader from '../../components/ui/PageHeader';
import QueryErrorState from '../../components/ui/QueryErrorState';
import StatCard from '../../components/ui/StatCard';

// ─── Helpers ────────────────────────────────────────────────────────────────

function centsToEuros(cents: number, decimals = 0): string {
  return (
    (cents / 100).toLocaleString('fr-FR', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }) + ' €'
  );
}

function monthShort(yearMonth: string): string {
  const [y, m] = yearMonth.split('-').map(Number);
  return new Date(y, (m ?? 1) - 1, 1).toLocaleDateString('fr-FR', { month: 'short' });
}

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Bonjour';
  if (h < 18) return 'Bon après-midi';
  return 'Bonsoir';
}

function currentYearMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function RevenueChart({ data }: { data: AdminStatsMonthlyRevenue[] }) {
  const current = currentYearMonth();
  const maxVolume = Math.max(...data.map(d => d.volumeCents), 1);

  if (data.length === 0) {
    return (
      <div className='flex items-center justify-center h-32 text-slate-300 text-sm'>
        Aucune donnée de revenus disponible
      </div>
    );
  }

  return (
    <div className='flex items-end gap-1.5 h-32 w-full'>
      {data.map(d => {
        const totalHeight = Math.round((d.volumeCents / maxVolume) * 100);
        const feeHeight = d.volumeCents > 0 ? Math.round((d.feeCents / d.volumeCents) * totalHeight) : 0;
        const isCurrent = d.month === current;

        return (
          <div
            key={d.month}
            className='flex-1 flex flex-col justify-end gap-0'
            title={`${monthShort(d.month)} — Commission : ${centsToEuros(d.feeCents)} / Volume : ${centsToEuros(d.volumeCents)}`}
          >
            <div className='flex flex-col justify-end rounded-t overflow-hidden' style={{ height: `${totalHeight}%` }}>
              {/* Volume minus fee (base) */}
              <div
                className={`w-full ${isCurrent ? 'bg-blue-200' : 'bg-slate-100'}`}
                style={{ height: `${totalHeight - feeHeight}%` }}
              />
              {/* Fee on top */}
              <div
                className={`w-full ${isCurrent ? 'bg-blue-600' : 'bg-blue-400'} rounded-t`}
                style={{ height: `${feeHeight}%`, minHeight: d.feeCents > 0 ? '3px' : '0' }}
              />
            </div>
            <p
              className={`text-center mt-1.5 text-[10px] leading-none ${isCurrent ? 'text-blue-600 font-bold' : 'text-slate-400'}`}
            >
              {monthShort(d.month)}
            </p>
          </div>
        );
      })}
    </div>
  );
}

function PlanBar({ label, value, total, color }: { label: string; value: number; total: number; color: string }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div>
      <div className='flex justify-between text-xs mb-1'>
        <span className='text-slate-600'>{label}</span>
        <span className='font-semibold text-slate-800'>
          {value.toLocaleString('fr-FR')} ({pct}%)
        </span>
      </div>
      <div className='h-2 bg-slate-100 rounded-full overflow-hidden'>
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function OrgRow({ org }: { org: AdminStatsRecentOrg }) {
  const initials = org.name
    .split(/\s+/)
    .slice(0, 2)
    .map(w => w[0] ?? '')
    .join('')
    .toUpperCase();
  const isForfait = org.plan === SubPlan.FORFAIT;

  return (
    <div className='flex items-center gap-3 py-2.5'>
      <div className='w-8 h-8 flex-shrink-0 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-xs font-bold'>
        {initials}
      </div>
      <div className='flex-1 min-w-0'>
        <p className='text-sm font-semibold text-slate-900 truncate'>{org.name}</p>
        <p className='text-xs text-slate-400'>{formatDateAbbreviatedFr(org.createdAt)}</p>
      </div>
      <div className='flex items-center gap-1.5 flex-shrink-0'>
        {isForfait && (
          <span className='inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700'>
            <Zap className='w-2.5 h-2.5' />
            Forfait
          </span>
        )}
      </div>
    </div>
  );
}

function PaymentRow({ payment }: { payment: AdminStatsRecentPayment }) {
  return (
    <div className='flex items-center gap-3 py-2.5'>
      <div className='w-8 h-8 flex-shrink-0 rounded-full bg-emerald-100 flex items-center justify-center'>
        <Euro className='w-4 h-4 text-emerald-600' />
      </div>
      <div className='flex-1 min-w-0'>
        <p className='text-sm font-semibold text-slate-900 truncate'>{payment.competition.name}</p>
        <p className='text-xs text-slate-400 truncate'>{payment.organization.name}</p>
      </div>
      <div className='text-right flex-shrink-0'>
        <p className='text-sm font-bold text-slate-900'>{centsToEuros(payment.amountCents)}</p>
        {payment.applicationFeeCents != null && payment.applicationFeeCents > 0 && (
          <p className='text-xs text-emerald-600'>+{centsToEuros(payment.applicationFeeCents)} com.</p>
        )}
      </div>
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

export default function AdminDashboard() {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);

  const { data: mfaStatus } = useQuery({
    queryKey: queryKeys.mfa.status(),
    queryFn: getMfaStatusApi,
    enabled: isAuthenticated,
  });

  const shouldShowMfaBanner = mfaStatus?.totpEnabled === false;

  const {
    data: stats,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: getAdminStatsApi,
    staleTime: 5 * 60 * 1000,
  });

  const revenueMonthly = useMemo<AdminStatsMonthlyRevenue[]>(
    () => stats?.revenue.monthly ?? [],
    [stats?.revenue.monthly],
  );

  // Alertes déduites des stats
  const alerts = useMemo(() => {
    if (!stats) return [];
    const items: { label: string; value: number; severity: 'warn' | 'ok' }[] = [
      {
        label: 'Organisations avec retard de paiement',
        value: stats.organizations.bySubscriptionStatus.PAST_DUE ?? 0,
        severity: (stats.organizations.bySubscriptionStatus.PAST_DUE ?? 0) > 0 ? 'warn' : 'ok',
      },
      {
        label: 'Comptes utilisateurs désactivés',
        value: stats.users.disabled,
        severity: stats.users.disabled > 0 ? 'warn' : 'ok',
      },
      {
        label: 'Organisateurs sans Connect Stripe',
        value: stats.organizations.total - stats.organizations.withConnect,
        severity: stats.organizations.total - stats.organizations.withConnect > 5 ? 'warn' : 'ok',
      },
    ];
    return items;
  }, [stats]);

  const kpiCards = useMemo(() => {
    if (!stats) return null;
    return [
      {
        icon: Users,
        iconBg: 'bg-blue-50',
        iconColor: 'text-blue-600',
        label: 'Utilisateurs',
        value: stats.users.total.toLocaleString('fr-FR'),
        trend: `+${stats.users.newThisMonth} ce mois`,
        subtitle: `${stats.users.verified.toLocaleString('fr-FR')} vérifiés`,
      },
      {
        icon: Building,
        iconBg: 'bg-purple-50',
        iconColor: 'text-purple-600',
        label: 'Clubs actifs',
        value: stats.clubs.active.toLocaleString('fr-FR'),
        trend: `+${stats.clubs.newThisMonth} ce mois`,
        subtitle: `${stats.clubs.total.toLocaleString('fr-FR')} total`,
      },
      {
        icon: Briefcase,
        iconBg: 'bg-amber-50',
        iconColor: 'text-amber-500',
        label: 'Organisateurs',
        value: stats.organizations.total.toLocaleString('fr-FR'),
        trend: `+${stats.organizations.newThisMonth} ce mois`,
        subtitle: `${stats.organizations.connectActive} Connect actifs`,
      },
      {
        icon: Euro,
        iconBg: 'bg-emerald-50',
        iconColor: 'text-emerald-600',
        label: 'Commissions ce mois',
        value: centsToEuros(stats.revenue.thisMonthFeeCents),
        trend: `${centsToEuros(stats.revenue.thisYearFeeCents)} cette année`,
        trendColor: 'text-blue-600',
        subtitle: `${stats.revenue.totalPaidPayments.toLocaleString('fr-FR')} paiements traités`,
      },
      {
        icon: Trophy,
        iconBg: 'bg-rose-50',
        iconColor: 'text-rose-500',
        label: 'Inscriptions ce mois',
        value: stats.registrations.thisMonth.toLocaleString('fr-FR'),
        trend: `${stats.registrations.thisYear.toLocaleString('fr-FR')} cette année`,
        trendColor: 'text-slate-500',
        subtitle: `${stats.registrations.confirmed.toLocaleString('fr-FR')} confirmées`,
      },
    ];
  }, [stats]);

  const quickActions = [
    { label: 'Utilisateurs', icon: UserCheck, route: ROUTES.ADMIN_USERS, color: 'bg-blue-600 hover:bg-blue-700' },
    { label: 'Clubs', icon: Building, route: ROUTES.ADMIN_CLUBS, color: 'bg-purple-600 hover:bg-purple-700' },
    {
      label: 'Paiements',
      icon: CreditCard,
      route: ROUTES.ADMIN_PAYMENTS,
      color: 'bg-emerald-600 hover:bg-emerald-700',
    },
    { label: 'Analytics', icon: BarChart3, route: ROUTES.ADMIN_ANALYTICS, color: 'bg-slate-600 hover:bg-slate-700' },
  ];

  return (
    <DashboardLayout sidebarConfig={adminSidebar}>
      <div className='p-8 space-y-6'>
        <PageHeader title={`${greeting()}, Super Admin`} subtitle="Vue d'ensemble de la plateforme BoulesConnect" />

        {/* Bandeau sécurité MFA */}
        {shouldShowMfaBanner && (
          <div className='flex items-start gap-4 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4'>
            <AlertTriangle className='mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600' />
            <div className='flex-1'>
              <p className='font-semibold text-amber-800'>Sécurisez votre compte Super Admin</p>
              <p className='mt-0.5 text-sm text-amber-700'>
                Activez l&apos;application d&apos;authentification (TOTP) et générez vos codes de secours.
              </p>
            </div>
            <Link
              to={ROUTES.PROFILE_SECURITY}
              className='flex-shrink-0 flex items-center gap-1.5 rounded-xl bg-amber-600 px-3 py-2 text-xs font-bold text-white hover:bg-amber-700 transition-colors'
            >
              <ShieldCheck className='h-3.5 w-3.5' />
              Activer
            </Link>
          </div>
        )}

        {/* ── KPI cards ── */}
        {isError && <QueryErrorState onRetry={() => void refetch()} />}
        {isLoading ? (
          <div className='grid grid-cols-2 lg:grid-cols-5 gap-5'>
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className='stat-card animate-pulse'>
                <div className='w-12 h-12 bg-slate-100 rounded-xl mb-4' />
                <div className='h-3 w-20 bg-slate-100 rounded-full mb-2' />
                <div className='h-8 w-24 bg-slate-100 rounded-full' />
              </div>
            ))}
          </div>
        ) : (
          kpiCards && (
            <div className='grid grid-cols-2 lg:grid-cols-5 gap-5'>
              {kpiCards.map(card => (
                <StatCard
                  key={card.label}
                  icon={card.icon}
                  iconBg={card.iconBg}
                  iconColor={card.iconColor}
                  label={card.label}
                  value={card.value}
                  trend={card.trend}
                  trendColor={card.trendColor}
                  subtitle={card.subtitle}
                />
              ))}
            </div>
          )
        )}

        {/* ── Revenue chart + Répartition ── */}
        <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
          {/* Revenue chart */}
          <div className='lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-6'>
            <div className='flex items-start justify-between mb-6'>
              <div>
                <h2 className='font-bold text-slate-900 text-lg'>Revenus mensuels</h2>
                <p className='text-sm text-slate-400 mt-0.5'>
                  Commissions <span className='inline-block w-2.5 h-2.5 rounded-sm bg-blue-600 align-middle' /> sur
                  volume <span className='inline-block w-2.5 h-2.5 rounded-sm bg-blue-100 align-middle' />
                </p>
              </div>
              {stats && (
                <div className='text-right'>
                  <p className='text-2xl font-extrabold text-slate-900'>{centsToEuros(stats.revenue.totalFeeCents)}</p>
                  <p className='text-xs text-slate-400'>commissions cumulées</p>
                </div>
              )}
            </div>
            {isLoading ? (
              <div className='h-32 flex items-end gap-1.5'>
                {Array.from({ length: 12 }).map((_, i) => (
                  <div
                    key={i}
                    className='flex-1 bg-slate-100 rounded-t animate-pulse'
                    style={{ height: `${30 + (i % 5) * 12}%` }}
                  />
                ))}
              </div>
            ) : (
              <RevenueChart data={revenueMonthly} />
            )}
            {stats && (
              <div className='grid grid-cols-3 gap-4 mt-5 pt-5 border-t border-slate-100'>
                <div>
                  <p className='text-xs text-slate-400 mb-0.5'>Volume total</p>
                  <p className='font-bold text-slate-900'>{centsToEuros(stats.revenue.totalVolumeCents)}</p>
                </div>
                <div>
                  <p className='text-xs text-slate-400 mb-0.5'>Cette année</p>
                  <p className='font-bold text-slate-900'>{centsToEuros(stats.revenue.thisYearFeeCents)}</p>
                </div>
                <div>
                  <p className='text-xs text-slate-400 mb-0.5'>Ce mois</p>
                  <p className='font-bold text-emerald-600'>{centsToEuros(stats.revenue.thisMonthFeeCents)}</p>
                </div>
              </div>
            )}
          </div>

          {/* Répartition + Alertes */}
          <div className='flex flex-col gap-6'>
            {/* Répartition plans organisateurs */}
            <div className='bg-white rounded-2xl border border-slate-200 p-6 flex-1'>
              <h2 className='font-bold text-slate-900 mb-4'>Répartition organisateurs</h2>
              {isLoading ? (
                <div className='space-y-4'>
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className='space-y-1.5 animate-pulse'>
                      <div className='h-3 w-32 bg-slate-100 rounded-full' />
                      <div className='h-2 bg-slate-100 rounded-full' />
                    </div>
                  ))}
                </div>
              ) : stats ? (
                <div className='space-y-4'>
                  <PlanBar
                    label='Plan Forfait'
                    value={stats.organizations.byPlan.FORFAIT}
                    total={stats.organizations.total}
                    color='bg-amber-400'
                  />
                  <PlanBar
                    label='Plan Commission'
                    value={stats.organizations.byPlan.COMMISSION}
                    total={stats.organizations.total}
                    color='bg-slate-300'
                  />
                  <PlanBar
                    label='Connect Stripe actif'
                    value={stats.organizations.connectActive}
                    total={stats.organizations.total}
                    color='bg-emerald-400'
                  />
                  <div className='flex items-center justify-between pt-2 border-t border-slate-100'>
                    <span className='text-xs text-slate-400'>Clubs Pro / Total</span>
                    <span className='text-xs font-bold text-slate-700'>
                      {stats.clubs.byPlan.PRO} / {stats.clubs.total}
                    </span>
                  </div>
                </div>
              ) : null}
            </div>

            {/* Alertes */}
            <div className='bg-white rounded-2xl border border-slate-200 p-6'>
              <h2 className='font-bold text-slate-900 mb-4'>Points d'attention</h2>
              {isLoading ? (
                <div className='space-y-3'>
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className='h-8 bg-slate-100 rounded-lg animate-pulse' />
                  ))}
                </div>
              ) : (
                <div className='space-y-2.5'>
                  {alerts.map(alert => (
                    <div
                      key={alert.label}
                      className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm ${
                        alert.severity === 'warn' && alert.value > 0
                          ? 'bg-amber-50 text-amber-800'
                          : 'bg-slate-50 text-slate-600'
                      }`}
                    >
                      <div className='flex items-center gap-2'>
                        {alert.severity === 'warn' && alert.value > 0 ? (
                          <AlertTriangle className='w-4 h-4 text-amber-500 flex-shrink-0' />
                        ) : (
                          <CheckCircle className='w-4 h-4 text-emerald-500 flex-shrink-0' />
                        )}
                        <span className='text-xs leading-tight'>{alert.label}</span>
                      </div>
                      <span
                        className={`font-bold text-xs ml-2 flex-shrink-0 ${
                          alert.severity === 'warn' && alert.value > 0 ? 'text-amber-700' : 'text-slate-400'
                        }`}
                      >
                        {alert.value}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Dernières orgs + Derniers paiements ── */}
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
          {/* Dernières organisations */}
          <div className='bg-white rounded-2xl border border-slate-200'>
            <div className='px-6 py-4 border-b border-slate-100 flex items-center justify-between'>
              <div className='flex items-center gap-2'>
                <Crown className='w-4 h-4 text-amber-500' />
                <h2 className='font-bold text-slate-900'>Derniers organisateurs inscrits</h2>
              </div>
              <Link
                to={ROUTES.ADMIN_ORGANIZERS}
                className='text-xs text-blue-600 font-semibold flex items-center gap-1 hover:gap-2 transition-all'
              >
                Voir tout <ArrowRight className='w-3.5 h-3.5' />
              </Link>
            </div>
            <div className='px-6 divide-y divide-slate-50'>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className='flex items-center gap-3 py-2.5 animate-pulse'>
                    <div className='w-8 h-8 rounded-full bg-slate-100 flex-shrink-0' />
                    <div className='flex-1 space-y-1.5'>
                      <div className='h-3 w-36 bg-slate-100 rounded-full' />
                      <div className='h-2.5 w-20 bg-slate-100 rounded-full' />
                    </div>
                  </div>
                ))
              ) : (stats?.recentOrganizations ?? []).length === 0 ? (
                <p className='py-8 text-center text-slate-300 text-sm'>Aucun organisateur récent</p>
              ) : (
                (stats?.recentOrganizations ?? []).map(org => <OrgRow key={org.id} org={org} />)
              )}
            </div>
          </div>

          {/* Derniers paiements */}
          <div className='bg-white rounded-2xl border border-slate-200'>
            <div className='px-6 py-4 border-b border-slate-100 flex items-center justify-between'>
              <div className='flex items-center gap-2'>
                <TrendingUp className='w-4 h-4 text-emerald-500' />
                <h2 className='font-bold text-slate-900'>Derniers paiements</h2>
              </div>
              <Link
                to={ROUTES.ADMIN_PAYMENTS}
                className='text-xs text-blue-600 font-semibold flex items-center gap-1 hover:gap-2 transition-all'
              >
                Voir tout <ArrowRight className='w-3.5 h-3.5' />
              </Link>
            </div>
            <div className='px-6 divide-y divide-slate-50'>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className='flex items-center gap-3 py-2.5 animate-pulse'>
                    <div className='w-8 h-8 rounded-full bg-slate-100 flex-shrink-0' />
                    <div className='flex-1 space-y-1.5'>
                      <div className='h-3 w-36 bg-slate-100 rounded-full' />
                      <div className='h-2.5 w-24 bg-slate-100 rounded-full' />
                    </div>
                    <div className='h-4 w-16 bg-slate-100 rounded-full' />
                  </div>
                ))
              ) : (stats?.recentPayments ?? []).length === 0 ? (
                <p className='py-8 text-center text-slate-300 text-sm'>Aucun paiement récent</p>
              ) : (
                (stats?.recentPayments ?? []).map(payment => <PaymentRow key={payment.id} payment={payment} />)
              )}
            </div>
          </div>
        </div>

        {/* ── Quick actions ── */}
        <div className='bg-white rounded-2xl border border-slate-200 p-6'>
          <h2 className='font-bold text-slate-900 mb-4'>Actions rapides</h2>
          <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
            {quickActions.map(action => {
              const Icon = action.icon;
              return (
                <button
                  key={action.label}
                  onClick={() => navigate(action.route)}
                  className={`${action.color} text-white rounded-xl p-4 flex items-center gap-3 transition font-semibold text-sm`}
                >
                  <div className='w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0'>
                    <Icon className='w-5 h-5' />
                  </div>
                  <span className='flex-1 text-left'>{action.label}</span>
                  <ArrowRight className='w-4 h-4 opacity-60' />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
